import { useEffect, useState, useMemo, useRef } from 'react';

import { addError } from '@shared/utils/logger';
import { useStandardHotelRoomPriceMapping } from './useListingPriceMapping';
import type { AccommodationsScenario, JoyHotelAccommodationListingDTO } from '@shared/types/Accommodations.types';
import { isValidProperty, transformPropertiesToListings } from '@shared/utils/accommodations/AccommodationTiles';
import type { SearchExtendedAccommodationPropertiesV2Payload } from '@graphql/generated-base';
import { useAccommodationsConsolePropertiesV2Query } from '../AccommodationsConsole.graphql.generated';
import { FetchStrategy } from '@graphql/generated-base';
import { useListingsPanelTelemetry } from '../components/ListingsPanel/ListingsPanel.telemetry';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { PriceSource } from '@shared/constants/accommodations';
import type { CashFundConfig } from '@shared/utils/cashFund/cashFund.types';
import { buildWalletSpendQueryVariables } from './walletSpendQueryVariables';
import type { FeaturedPlacementVariant } from './useFeaturedPlacements.types';

const BACKGROUND_REFRESH_MIN_RADIUS_KM = 5;

interface UseBackgroundPricingRefreshArgs
  extends Readonly<{
    isFetchingListings: boolean;
    hasReceivedRegionalData: boolean;
    regionalHotelsPayload: SearchExtendedAccommodationPropertiesV2Payload;
    locationName?: string;
    /** Must match what the first fetch sent, or this refresh replaces the tiles with split-less ones. */
    cashFund?: CashFundConfig;
    /**
     * Reported on `destinationSearched` so requested/received sponsored counts can be read per arm.
     * Undefined for the scenarios outside the experiment (`ota`, `cashFund`, `honeymoon`).
     */
    featuredPlacementVariant?: FeaturedPlacementVariant;
    /**
     * Reported alongside the variant. `guest` and `admin` are both in the experiment but only guest
     * renders the featured unit, so a fill rate that pools them is misleading.
     */
    scenario?: AccommodationsScenario;
    /** Set by the batched-live-pricing experiment: that path prices the same hotels by id instead, so
     *  this whole-list refetch must not also run. */
    skip?: boolean;
  }> {}

interface BackgroundPricingRefreshResult
  extends Readonly<{
    isLoading: boolean;
    error: Error | undefined;
    listings: ReadonlyArray<JoyHotelAccommodationListingDTO>;
  }> {}

export const useBackgroundPricingRefresh = (args: UseBackgroundPricingRefreshArgs): BackgroundPricingRefreshResult => {
  const { regionalHotelsPayload, isFetchingListings, hasReceivedRegionalData, locationName, cashFund, featuredPlacementVariant, scenario, skip } = args;

  const [listings, setListings] = useState<readonly JoyHotelAccommodationListingDTO[]>([]);

  const telemetry = useListingsPanelTelemetry();
  const searchStartTimeRef = useRef<number | null>(null);

  const mapHotelRoomPrice = useStandardHotelRoomPriceMapping();

  const payload = useMemo(() => {
    if (!regionalHotelsPayload?.customSearchParams) {
      return null;
    }

    return {
      eventHandle: regionalHotelsPayload.eventHandle,
      customSearchParams: {
        // Spread wholesale, which is what keeps `maxSponsoredSlots` identical to the first search.
        // It has to stay that way: if this refresh asked for a different number of sponsored slots,
        // its response would disagree with the initial one about sponsored inventory and the tiles
        // would swap under the guest when the realtime prices land. Only the three fields below may
        // differ between the two queries.
        ...regionalHotelsPayload.customSearchParams,
        // These results replace the first query's, so never search narrower than it did — a fixed
        // 5km would shrink a honeymoon destination search back to the resorts nearest the pin.
        radiusKM: Math.max(BACKGROUND_REFRESH_MIN_RADIUS_KM, regionalHotelsPayload.customSearchParams.radiusKM ?? 0),
        useRealtimePricing: true,
        fetchStrategy: FetchStrategy.PREFER_VENDOR,
      },
    };
  }, [regionalHotelsPayload]);

  const shouldSkipQuery = skip || !payload || isFetchingListings || !hasReceivedRegionalData || isBotClient();

  useEffect(() => {
    setListings([]);
    searchStartTimeRef.current = Date.now();
  }, [regionalHotelsPayload]);

  const { data, error, loading } = useAccommodationsConsolePropertiesV2Query({
    batchMode: 'off',
    skip: shouldSkipQuery,
    fetchPolicy: 'network-only',
    variables: { payload: payload!, ...buildWalletSpendQueryVariables(cashFund) },
  });

  useEffect(() => {
    const properties = data?.searchExtendedAccommodationProperties?.properties;
    if (!properties) {
      return;
    }

    const validProperties = properties.filter(isValidProperty);
    // PREFER_VENDOR with useRealtimePricing true, so these prices come from a realtime vendor fetch
    // and replace the snapshot prices the first query rendered.
    const newListings = transformPropertiesToListings(validProperties, mapHotelRoomPrice, PriceSource.LIVE);
    setListings(newListings);

    // Fires once per search, not per render: the ref is set when the payload changes and nulled
    // here, so re-renders, pagination and filter changes cannot re-fire it.
    if (searchStartTimeRef.current !== null) {
      const responseTimeMs = Date.now() - searchStartTimeRef.current;
      telemetry.destinationSearched({
        googlePlaceId: regionalHotelsPayload?.customSearchParams?.location?.context?.googlePlaceId,
        locationName,
        responseTimeMs,
        // One refetch replaces every price at once, so the first live price is the last.
        msToFirstLivePrice: responseTimeMs,
        livePricingPath: 'refetch',
        resultCount: validProperties.length,
        // Fill rate for paid placement. `sponsoredSlotsRequested` is what we asked for and
        // `sponsoredResultCount` is what came back; they diverge whenever the contracted slot count
        // is smaller than the arm's ceiling (notably `row`, which asks for 5). Requested is read off
        // the payload rather than recomputed, so it is literally the number that was sent — and it
        // is undefined exactly when the field was omitted and the server's configured count applied.
        sponsoredSlotsRequested: payload?.customSearchParams?.maxSponsoredSlots ?? undefined,
        sponsoredResultCount: newListings.filter(listing => listing.isSponsored === true).length,
        featuredPlacementVariant,
        // Admin is in the experiment but never renders the featured unit, so guest and admin fill
        // rates must be separable here or the pooled number reads as a treatment effect it isn't.
        scenario,
      });
      searchStartTimeRef.current = null;
    }
  }, [data, mapHotelRoomPrice]); // eslint-disable-line react-hooks/exhaustive-deps -- regionalHotelsPayload, payload, locationName and featuredPlacementVariant are only used for telemetry context

  useEffect(() => {
    if (error) {
      addError(error, {
        errorMessage: error.message,
        context: 'useBackgroundPricingRefresh',
        source: 'Background pricing refresh failed (AccommodationsConsole)',
      });
    }
  }, [error]);

  return {
    error,
    listings,
    isLoading: loading,
  };
};
